import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Mail, Phone, MapPin } from 'lucide-react';
import api from '../api';
import ParentHeader from './components/Header';

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parentData, setParentData] = useState(null);
  const [children, setChildren] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [todayEvents, setTodayEvents] = useState([]);
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [orgInfo, setOrgInfo] = useState({ 
    name: '', 
    logoUrl: '', 
    address: '', 
    phone: '', 
    email: '' 
  });
  const [stats, setStats] = useState({
    totalChildren: 0,
    activeChildren: 0,
    presentToday: 0,
    absentToday: 0,
  });

  // ✅ Fetch organization info FIRST
  const fetchOrgInfo = async () => {
    try {
      const tenantId = localStorage.getItem('tenantId');
      const token = localStorage.getItem('token');
      
      console.log('🔍 [Parent] Fetching org info for tenant:', tenantId);
      
      if (tenantId) {
        const response = await api.get(`/api/organization/basic-info/${tenantId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        console.log('📦 [Parent] Org info response:', response.data);
        
        if (response.data && response.data.success) {
          const orgData = {
            name: response.data.name || '',
            logoUrl: response.data.logoUrl || '',
            address: response.data.address || '',
            phone: response.data.phone || '',
            email: response.data.email || ''
          };
          setOrgInfo(orgData);
          
          // Cache org info
          const existing = JSON.parse(localStorage.getItem('tenantData') || '{}');
          const updated = {
            ...existing,
            institutionName: orgData.name,
            logoUrl: orgData.logoUrl,
            address: orgData.address,
            phone: orgData.phone,
            email: orgData.email
          };
          localStorage.setItem('tenantData', JSON.stringify(updated));
          console.log('✅ [Parent] Org info set:', orgData);
        } else {
          // Try to get from localStorage
          const t = JSON.parse(localStorage.getItem('tenantData') || '{}');
          if (t.institutionName || t.name) {
            setOrgInfo({
              name: t.institutionName || t.name || '',
              logoUrl: t.logoUrl || '',
              address: t.address || '',
              phone: t.phone || '',
              email: t.email || ''
            });
          }
        }
      }
    } catch (err) {
      console.error('❌ [Parent] Error fetching org info:', err);
      // Try to get from localStorage as fallback
      try {
        const t = JSON.parse(localStorage.getItem('tenantData') || '{}');
        if (t.institutionName || t.name) {
          setOrgInfo({
            name: t.institutionName || t.name || '',
            logoUrl: t.logoUrl || '',
            address: t.address || '',
            phone: t.phone || '',
            email: t.email || ''
          });
        }
      } catch (e) {
        console.error('Error reading from localStorage:', e);
      }
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const profileRes = await api.get('/api/parent/profile');
      if (profileRes.data.success) {
        const data = profileRes.data.parentInfo;
        setParentData(data);
        const childrenList = data.children || [];
        setChildren(childrenList);

        const activeChildren = childrenList.filter(c => c.status === 'active').length;

        const eventsRes = await api.get('/api/parent/today-events');
        if (eventsRes.data.success) {
          setTodayEvents(eventsRes.data.data.todayEvents || []);
        }

        // Fetch attendance for first child (backend defaults to today)
        if (childrenList.length > 0) {
          const firstChild = childrenList[0];
          const attendanceRes = await api.get(`/api/parent/child/${firstChild.studentUserId}/attendance`);
          if (attendanceRes.data.success) {
            const data = attendanceRes.data.data;
            setAttendanceSummary(data.summary);
            setStats({
              totalChildren: childrenList.length,
              activeChildren: activeChildren,
              presentToday: data.summary?.present || 0,
              absentToday: data.summary?.absent || 0,
            });
          }
        }

        setStats(prev => ({
          ...prev,
          totalChildren: childrenList.length,
          activeChildren: activeChildren,
        }));
      }

      const notifRes = await api.get('/api/parent/notifications');
      if (notifRes.data.success) {
        setNotifications(notifRes.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Unable to load your dashboard right now. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Load org info and data on mount
  useEffect(() => {
    const loadAllData = async () => {
      await fetchOrgInfo();
      await fetchData();
    };
    loadAllData();
  }, []);

  const handleViewChild = (studentId) => {
    const child = children.find(c => c.studentUserId === studentId);
    navigate('/parent/child', {
      state: {
        studentId: studentId,
        childData: child,
      },
    });
  };

  const getInitials = (name) => {
    if (!name) return 'P';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getAcademicLabel = (child) => {
    const ctx = child.academicContext;
    if (!ctx) return null;

    const parts = [];
    if (ctx.programTypeName) parts.push({ label: 'Level', value: ctx.programTypeName });
    if (ctx.programName) parts.push({ label: 'Course/Stream', value: ctx.programName });
    if (ctx.academicYearName) parts.push({ label: 'School Year', value: ctx.academicYearName });
    
    if (ctx.isSemesterBased && ctx.semester) {
      parts.push({ label: 'Term/Semester', value: ctx.semester.name });
    } else if (ctx.term) {
      parts.push({ label: 'Term/Semester', value: ctx.term.label });
    }
    
    if (child.class) parts.push({ label: 'Class/Section', value: child.class });
    return parts;
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-[3px] border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-500 text-xs font-semibold tracking-wide">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50">
        <ParentHeader parentData={parentData} notifications={notifications} orgInfo={orgInfo} />
        <div className="flex items-center justify-center p-4 mt-10">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-xl p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-rose-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-rose-100">
              <svg className="w-8 h-8 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-slate-950 mb-2">Something went wrong</h3>
            <p className="text-slate-500 text-sm mb-6 leading-relaxed">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="w-full sm:w-auto px-6 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-xl transition-all"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950 antialiased selection:bg-indigo-100">
      <ParentHeader 
        parentData={parentData} 
        notifications={notifications} 
        orgInfo={orgInfo} 
      />

      <main className="max-w-6xl mx-auto px-4 py-6 sm:py-8 space-y-6">
        
        <div className="space-y-1">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900">
            Welcome back, {parentData?.parentName || 'Parent'} 👋
          </h1>
          <p className="text-sm text-slate-500 font-medium">
            {getTodayDate()} • Here's what's happening with your children today
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Children</p>
              <p className="text-2xl font-extrabold text-slate-950 mt-1">{stats.totalChildren}</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Enrolled Active</p>
              <p className="text-2xl font-extrabold text-emerald-600 mt-1">{stats.activeChildren}</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Present Today</p>
              <p className="text-2xl font-extrabold text-blue-600 mt-1">{stats.presentToday}</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Absent Today</p>
              <p className="text-2xl font-extrabold text-rose-600 mt-1">{stats.absentToday}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          
          {/* Children Section */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900 tracking-tight">My Children</h2>
              <span className="text-xs font-bold text-slate-500 bg-white border border-slate-200 px-2.5 py-1 rounded-lg shadow-sm">
                {children.length} Enrolled
              </span>
            </div>

            {children.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center shadow-sm">
                <h3 className="text-sm font-bold text-slate-900 mb-0.5">No profiles linked yet</h3>
                <p className="text-xs text-slate-400 max-w-xs mx-auto">Please check in with the school office administration to connect your children's profiles.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {children.map((child) => {
                  const academicParts = getAcademicLabel(child);
                  const isChildActive = child.status === 'active';
                  return (
                    <div
                      key={child.studentUserId}
                      onClick={() => handleViewChild(child.studentUserId)}
                      className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm hover:shadow-md hover:border-slate-200 transition-all cursor-pointer group flex flex-col justify-between min-h-[280px]"
                    >
                      <div className="space-y-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-bold text-white shadow-sm flex-shrink-0 border transition-all ${
                              isChildActive ? 'bg-gradient-to-tr from-indigo-600 to-violet-500 border-indigo-200' : 'bg-slate-400 border-slate-300'
                            }`}>
                              {getInitials(child.name)}
                            </div>
                            <div className="min-w-0">
                              <h3 className="font-bold text-slate-900 text-sm truncate group-hover:text-indigo-600 transition-colors">{child.name}</h3>
                              <p className="text-xs text-slate-400 font-medium truncate">{child.class || 'Class pending'}</p>
                            </div>
                          </div>
                          <span className={`inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-lg border flex-shrink-0 ${
                            isChildActive ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'
                          }`}>
                            {isChildActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>

                        {academicParts && academicParts.length > 0 && (
                          <div className="grid grid-cols-2 gap-2">
                            {academicParts.slice(0, 4).map((part, idx) => (
                              <div key={idx} className="bg-slate-50/70 border border-slate-100 rounded-xl p-2 min-w-0">
                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider truncate">{part.label}</p>
                                <p className="text-xs font-semibold text-slate-700 truncate">{part.value}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="space-y-3 mt-4 pt-3 border-t border-slate-100">
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <p className="text-slate-400 text-[10px] font-medium">Roll No / USN</p>
                            <p className="font-semibold text-slate-700 truncate">{child.usn || '—'}</p>
                          </div>
                          <div>
                            <p className="text-slate-400 text-[10px] font-medium">Admission ID</p>
                            <p className="font-semibold text-slate-700 truncate">{child.admissionNumber || '—'}</p>
                          </div>
                        </div>

                        <button
                          className="w-full py-2 text-xs font-bold text-indigo-600 bg-indigo-50/60 hover:bg-indigo-50 rounded-xl border border-indigo-100/30 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewChild(child.studentUserId);
                          }}
                        >
                          View Performance →
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right Side - Events & Attendance */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-slate-900 text-sm tracking-tight">Today's School Events</h3>
                <span className="text-[10px] font-bold text-slate-500 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded-md">
                  {todayEvents.length}
                </span>
              </div>

              {todayEvents.length === 0 ? (
                <div className="text-center py-4 space-y-1.5">
                  <p className="text-xs text-slate-400 font-medium">No school activities announced for today.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[260px] overflow-y-auto no-scrollbar">
                  {todayEvents.map((event, index) => {
                    const typeColors = 
                      event.eventType === 'holiday' ? 'bg-rose-500 text-rose-700 border-rose-100 bg-rose-50/50' :
                      event.eventType === 'exam' ? 'bg-amber-500 text-amber-700 border-amber-100 bg-amber-50/50' :
                      'bg-indigo-500 text-indigo-700 border-indigo-100 bg-indigo-50/50';

                    return (
                      <div key={index} className="flex items-start gap-2.5 p-3 rounded-xl border border-slate-100 bg-slate-50/40">
                        <div className={`w-1.5 h-1.5 mt-1.5 rounded-full flex-shrink-0 ${typeColors.split(' ')[0]}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-slate-800 truncate">{event.eventName}</p>
                          <p className="text-[11px] font-medium text-slate-400 truncate">
                            {event.isAllDay ? 'All Day' : `${event.startTime || ''} - ${event.endTime || ''}`}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {children.length > 0 && (
              <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-slate-900 text-sm tracking-tight">Today's Attendance</h3>
                  <span className="text-[10px] font-medium text-slate-400">
                    {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 items-center text-center">
                  <div className="bg-slate-50/60 border border-slate-100 rounded-xl p-2">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Present</p>
                    <p className="text-lg font-black text-slate-900 mt-0.5">{attendanceSummary?.present || 0}</p>
                  </div>
                  <div className="bg-slate-50/60 border border-slate-100 rounded-xl p-2">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Absent</p>
                    <p className="text-lg font-black text-slate-900 mt-0.5">{attendanceSummary?.absent || 0}</p>
                  </div>
                  <div className="bg-indigo-50/40 border border-indigo-100/60 rounded-xl p-2">
                    <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">Score</p>
                    <p className="text-lg font-black text-indigo-600 mt-0.5">{attendanceSummary?.percentage || 0}%</p>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-[10px] font-medium text-slate-400">
                    <span>0%</span>
                    <span>{attendanceSummary?.percentage || 0}%</span>
                    <span>100%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-indigo-600 rounded-full transition-all duration-500 shadow-sm" 
                      style={{ width: `${attendanceSummary?.percentage || 0}%` }} 
                    />
                  </div>
                </div>
                {attendanceSummary?.total === 0 && (
                  <p className="text-center text-[10px] text-amber-600 font-medium">
                    No attendance recorded for today
                  </p>
                )}
              </div>
            )}
          </div>

        </div>

        {/* ✅ New: Institution Contact Details Card */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <Building2 size={18} className="text-indigo-600" />
            <h3 className="text-sm font-bold text-slate-900">Institution Contact Details</h3>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Address */}
            {orgInfo.address && (
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                <MapPin size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Address</p>
                  <p className="text-sm font-medium text-slate-700 leading-relaxed">{orgInfo.address}</p>
                </div>
              </div>
            )}

            {/* Phone */}
            {orgInfo.phone && (
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                <Phone size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Phone</p>
                  <p className="text-sm font-medium text-slate-700">{orgInfo.phone}</p>
                </div>
              </div>
            )}

            {/* Email */}
            {orgInfo.email && (
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                <Mail size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Email</p>
                  <p className="text-sm font-medium text-slate-700 truncate">{orgInfo.email}</p>
                </div>
              </div>
            )}

            {/* Institution Name */}
            {orgInfo.name && (
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                <Building2 size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Institution</p>
                  <p className="text-sm font-medium text-slate-700">{orgInfo.name}</p>
                </div>
              </div>
            )}
          </div>
        </div>

      </main>
    </div>
  );
};

export default Dashboard;